import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, UserPlus, PencilLine, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasRole } from "@/lib/roles";
import { useConfirm } from "@/components/ConfirmProvider";

const TABS = [["pending", "Pending"], ["approved", "Approved"], ["rejected", "Rejected"]];
const label = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const show = (v) => (v === null || v === undefined || v === "" ? "—" : typeof v === "object" ? JSON.stringify(v) : String(v));
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");
const STATUS_TONE = { pending: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-800", rejected: "bg-red-100 text-red-700" };

function ChangeRows({ r }) {
    const rows = r.type === "create"
        ? Object.entries(r.payload).filter(([, v]) => v !== "" && v !== null && !(Array.isArray(v) && !v.length) && v !== 0).map(([field, v]) => ({ field, old: null, new: v }))
        : r.changes;
    return (
        <table className="mt-4 w-full text-sm" data-testid={`emp-change-diff-${r.id}`}>
            <thead><tr className="text-left text-ink/45 text-xs"><th className="py-1.5 pr-4 font-medium">Field</th>{r.type === "update" && <th className="py-1.5 pr-4 font-medium">Current</th>}<th className="py-1.5 font-medium">{r.type === "update" ? "New" : "Value"}</th></tr></thead>
            <tbody>
                {rows.map((c) => (
                    <tr key={c.field} className="border-t border-ink/5 align-top">
                        <td className="py-1.5 pr-4 text-ink/60 whitespace-nowrap">{label(c.field)}</td>
                        {r.type === "update" && <td className="py-1.5 pr-4 text-ink/45 line-through break-all">{show(c.old)}</td>}
                        <td className="py-1.5 text-ink font-medium break-all">{show(c.new)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function RequestCard({ r, canDecide, onDecide }) {
    const Icon = r.type === "create" ? UserPlus : PencilLine;
    return (
        <div className="bg-white border border-ink/10 p-5 sm:p-6" data-testid={`emp-change-${r.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-klein/10 text-klein flex items-center justify-center shrink-0"><Icon className="w-5 h-5" /></div>
                    <div>
                        <div className="font-semibold text-ink">
                            {r.type === "create" ? "New employee: " : "Edit: "}
                            {r.employee_id && r.status !== "pending" || r.type === "update" ? <Link to={`/employees/${r.employee_id}`} className="hover:underline">{r.employee_name}</Link> : r.employee_name}
                        </div>
                        <div className="text-xs text-ink/50 flex items-center gap-1"><Clock className="w-3 h-3" /> {r.requested_by_name} · {when(r.requested_at)}</div>
                    </div>
                </div>
                <span className={`text-xs font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full ${STATUS_TONE[r.status]}`} data-testid={`emp-change-status-${r.id}`}>{r.status}</span>
            </div>
            <ChangeRows r={r} />
            {r.status !== "pending" && (
                <div className="mt-3 text-xs text-ink/55">{r.status === "approved" ? "Approved" : "Rejected"} by {r.reviewed_by} · {when(r.reviewed_at)}{r.reason && <span className="block mt-1 text-red-700">Reason: {r.reason}</span>}</div>
            )}
            {canDecide && r.status === "pending" && (
                <div className="mt-4 flex gap-2">
                    <button onClick={() => onDecide(r, true)} className="inline-flex items-center gap-1.5 bg-emerald-600 text-white px-4 py-2 text-sm hover:bg-emerald-700" data-testid={`emp-change-approve-${r.id}`}><Check className="w-4 h-4" /> Approve</button>
                    <button onClick={() => onDecide(r, false)} className="inline-flex items-center gap-1.5 border border-red-300 text-red-700 px-4 py-2 text-sm hover:bg-red-50" data-testid={`emp-change-reject-${r.id}`}><X className="w-4 h-4" /> Reject</button>
                </div>
            )}
        </div>
    );
}

export default function EmployeeApprovals() {
    const { user } = useAuth();
    const { confirm, prompt } = useConfirm();
    const canDecide = hasRole(user, "admin", "hr");
    const [tab, setTab] = useState("pending");
    const [rows, setRows] = useState(null);

    const load = useCallback(() => api.get("/employee-change-requests", { params: { status: tab } }).then(({ data }) => setRows(data)).catch(() => setRows([])), [tab]);
    useEffect(() => { setRows(null); load(); }, [load]);

    const decide = async (r, approve) => {
        let reason = "";
        if (approve) {
            if (!(await confirm({ title: "Approve this change?", description: r.type === "create" ? `${r.employee_name} will be added as an employee.` : `The changes to ${r.employee_name} will be saved.`, confirmLabel: "Approve" }))) return;
        } else {
            reason = await prompt({ title: "Reject this change", description: "Tell the clerk what to fix.", placeholder: "Reason", confirmLabel: "Reject" });
            if (!reason || !reason.trim()) return;
        }
        try {
            await api.post(`/employee-change-requests/${r.id}/${approve ? "approve" : "reject"}`, approve ? {} : { reason });
            toast.success(approve ? "Approved and saved" : "Rejected — the clerk has been notified");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Action failed");
        }
    };

    return (
        <div className="p-6 sm:p-10 max-w-5xl" data-testid="employee-approvals-page">
            <div className="overline">Employee records</div>
            <h1 className="mt-2 font-display font-extrabold text-4xl sm:text-5xl text-ink">{canDecide ? "Clerk change approvals" : "My submitted changes"}</h1>
            <p className="mt-3 text-sm text-ink/60">{canDecide ? "New employees and edits made by HR Clerks wait here until Admin or HR approves them." : "Your new employees and edits are saved once Admin or HR approves them."}</p>
            <div className="mt-8 flex gap-2" data-testid="emp-change-tabs">
                {TABS.map(([k, l]) => (
                    <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 text-sm rounded-full border ${tab === k ? "bg-ink text-white border-ink" : "border-ink/15 text-ink/70 hover:bg-ink/5"}`} data-testid={`emp-change-tab-${k}`}>{l}</button>
                ))}
            </div>
            <div className="mt-6 space-y-4">
                {rows === null && <div className="text-sm text-ink/50">Loading…</div>}
                {rows?.length === 0 && <div className="bg-white border border-ink/10 p-10 text-center text-sm text-ink/50" data-testid="emp-change-empty">Nothing {tab} right now.</div>}
                {rows?.map((r) => <RequestCard key={r.id} r={r} canDecide={canDecide} onDecide={decide} />)}
            </div>
        </div>
    );
}
